import { createSelector } from 'reselect';

export const getSourcesLoaded = (state) => state.sources.siteLoaded;

export const getSources = (state) => state.sources.sources;

export const getSourceTypes = (state) => state.sources.sourceTypes;

export const getInjectableSourceTypes = (state) => state.sources.injectableSourceTypes;

export const getImportableSourceTypes = (state) => state.sources.importableSourceTypes;

export const getSyncedSources = (state) => state.sources.syncedSourceTypes;

export const getSyncHealth = (state, siteId) => state.sources.syncHealth[siteId];

export const getSiteVariablesIndex = (state) => state.sources.siteVariablesIndex;

export const getSiteVariablesUpdatedAt = (state) => state.sources.siteVariablesUpdatedAt;

// getSiteVariablesUpdatedAt is included as an input selector in order to trigger a recomputation when
// single variables are updated or removed, which doesn't change the siteVariablesIndex object reference
// but should trigger updates in components relying on getSiteVariables selector to get the updated variable data.
export const getSiteVariables = createSelector(
  [getSiteVariablesIndex, getSiteVariablesUpdatedAt],
  (variableIndex = {}) => Object.values(variableIndex)
);

export const getSourceConnectivity = (state) => state.sources.sourceConnectivity;

// Consumed by `SourceConnectivityNotification` (shows "<name> is disconnected!").
// Only concerned with / sends notifications for the disconnected state (offline (0)).
export const getAggregatedSourceConnectivityByName = createSelector(
  (state) => state.sources.sourceConnectivity || [],
  (sourceConnectivity) =>
    Object.fromEntries(sourceConnectivity.map((source) => [source.name, source.status]))
);

// Consumed by `ConnectivityStatus` component (connectivity icon in header)
export const getAggregatedStationStatus = createSelector(
  (state) => state.sources.sourceConnectivity || [],
  (connectedSources) => {
    const stationStatuses = new Set(
      connectedSources
        .map((source) => source.status)
        .filter((status) => Number.isInteger(status) && [0, 1, 2].includes(status))
    );
    if (stationStatuses.size === 0) return 2;
    return stationStatuses.has(0) ? 0 : Math.max(...stationStatuses);
  }
);

/*
 * Sent with requests for timeseries data and based on the non-aggregated (raw) connectivity status
 * of the source, as sent from the backend. If a source is reported as offline, 3rd party requests
 * are not made.
 *
 * In on-premise deployments, we should always have access to the source (MIMS db), otherwise
 * the application would not be able to retrieve any data.
 */
export const getRawSourceConnectivityById = createSelector(
  (state) => state.sources.sourceConnectivity || [],
  (sourceConnectivity) =>
    Object.fromEntries(
      sourceConnectivity.map((source) => [source.sourceId, source.rawStatus ?? source.status])
    )
);

export const getVariableById = createSelector(
  [(_, variableId) => variableId, getSiteVariablesIndex],
  (variableId, variableIndex = {}) => variableIndex[variableId]
);

export const getLinkedSources = createSelector(getSources, (sources) =>
  sources.filter((s) => s.options?.siteSourceLink)
);
